import type { AgeClass, AnimalSex } from "@/api/cases/CasesApi";
import { CareProtocolApi } from "@/api/careProtocol/CareProtocolApi";
import {
  useCreateAnimalCasesBatch,
  useIntakeReasons,
} from "@/api/cases/useCases";
import { CareSitesApi } from "@/api/careSites/CareSitesApi";
import { usePersistFinderForCases } from "@/api/contacts/useContacts";
import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { useOrganizationSpecies } from "@/api/species/useOrganizationSpecies";
import { useAuth } from "@/auth/AuthContext";
import { useNetworkStatus } from "@/network/NetworkContext";
import { queryKeys } from "@/api/queryKeys";
import { LOADING_LABEL } from "@/constants/uiLabels";
import { WIDE_VIEWPORT_BREAKPOINT } from "@/constants/layout";
import { BatchAnimalEditor } from "@/ui/intake/BatchAnimalEditor";
import { batchWeightFocusTarget } from "@/ui/intake/batchAnimalLayout";
import { useBatchAnimalRow } from "@/ui/intake/useBatchAnimalRow";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { FloatingLabelDatePicker } from "@/ui/components/FloatingLabelDatePicker";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { FloatingLabelListPicker } from "@/ui/components/FloatingLabelListPicker";
import {
  formatPlaceLine,
  GeoPlaceAutocompleteField,
} from "@/ui/components/GeoPlaceAutocompleteField";
import { EmptyState } from "@/ui/components/EmptyState";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { finderCaseHref } from "@/ui/finder/finderCaseNotice";
import { FinderIntakeCard } from "@/ui/finder/FinderIntakeCard";
import { FinderOfflineChoice } from "@/ui/finder/FinderOfflineChoice";
import { FinderPrivacySaveChoice } from "@/ui/finder/FinderPrivacySaveChoice";
import {
  useClearFinderDraftAfterSavedCase,
  useFinderMode,
} from "@/ui/finder/FinderModeContext";
import {
  emptyFinderDraft,
  FINDER_BATCH_FAILED,
  FINDER_NAME_SAVE_HINT,
  FINDER_OFFLINE_FINDER,
  FINDER_PARTIAL_IDENTITY_LOCKED,
  FINDER_PARTIAL_IDENTITY_RESTORED,
  FINDER_SAVED_FINDER_FAILED,
  FINDER_STILL_ACTIVE,
  finderDraftHasPayload,
  finderModeAllowsFinalize,
  intakeQueuedLocally,
  finderPayloadWithoutName,
  finderSaveBlockedWithoutPrivacy,
  finderSaveBlockedWithoutProvision,
  isFinderDraftReady,
} from "@/ui/finder/finderMode";
import { IntakeReasonChips } from "@/ui/intake/IntakeReasonChips";
import { IntakeSpeciesField } from "@/ui/intake/IntakeSpeciesField";
import { prefillCareSiteId } from "@/ui/intake/intakeCareSite";
import { intakeSharedSpeciesLabel, intakeSpeciesForSave, intakeSpeciesMissing } from "@/ui/intake/intakeSpecies";
import { batchIntakeReceiptText } from "@/ui/intake/intakeReceipt";
import {
  FUND_DATE_CHECK_HINT,
  INTAKE_KEPT_LINE,
  INTAKE_PLACE_HINT,
  SPECIES_FREE_TEXT_MAX,
  SPECIES_FREE_TEXT_TOO_LONG,
  SPECIES_CHECK_HINT,
} from "@/ui/intake/intakeCopy";
import { intakeStatusLiveProps, shouldAutofocusIntakeWeight } from "@/ui/intake/intakeFocus";
import {
  armIntakeFollowup,
  clearIntakeFollowupNotice,
  consumeIntakeWeightFocus,
  rememberIntakeCases,
  requestIntakeWeightFocus,
} from "@/ui/intake/lastCareSite";
import { useCoarseTarget } from "@/ui/intake/useCoarseTarget";
import { useIntakeFollowupNotice } from "@/ui/intake/useIntakeFollowupNotice";
import { useIntakeCareSites } from "@/ui/intake/useIntakeCareSites";
import {
  invalidateIntakeWeightQueries,
  INTAKE_WEIGHT_RETRY_FALLBACK,
  INTAKE_WEIGHT_RIGHTS_MESSAGE,
  intakeWeightErrorText,
  intakeWeightEvent,
  parseIntakeWeight,
  settleIntakeWeights,
  type IntakeWeightOutcome,
  type IntakeWeightSlot,
} from "@/ui/intake/intakeWeight";
import {
  foundPlaceForSave,
  foundPlaceNeedsHint,
  FUNDORT_UNSELECTED_HINT,
} from "@/utils/foundPlaceDraft";
import {
  assessFoundOn,
  formatIsoDateDE,
  intakeErrorMessage,
  todayIsoLocal,
  uiErrorMessage,
} from "@/utils/isoDate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applySharedIntake,
  markOwnIntakeFields,
  type SharedIntakeFieldKey,
  type SharedIntakeFields,
} from "@/ui/intake/applySharedIntake";
import { viewWhileLoading } from "@/ui/loadingState";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";

function newClientId(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type AnimalDraft = {
  clientCreateId: string;
  name: string;
  sex: AnimalSex;
  ageClass: AgeClass;
  careSiteId: string | null;
  placeId: string | null;
  foundOn: string | null;
  foundPlaceId: string | null;
  foundPlaceText: string;
  foundPlaceExtra: string;
  selectedReasons: Set<string>;
  reasonNote: string;
  notes: string;
  intakeWeight: string;
  /** Zuletzt angebotene gemeinsame Angaben. */
  sharedOrigin?: SharedIntakeFields;
  /** Felder, die dieses Tier bewusst selbst gesetzt hat. */
  ownFields: Set<SharedIntakeFieldKey>;
};

function blankAnimal(defaults?: Partial<AnimalDraft>): AnimalDraft {
  return {
    clientCreateId: newClientId(),
    name: "",
    sex: "unknown",
    ageClass: "unclear",
    careSiteId: null,
    placeId: null,
    // Leer, nicht „heute“: sonst gilt das Datum als eigener Wert und ein
    // abweichendes gemeinsames Funddatum erreicht die Start-Tiere nicht.
    foundOn: null,
    foundPlaceId: null,
    foundPlaceText: "",
    foundPlaceExtra: "",
    selectedReasons: new Set(),
    reasonNote: "",
    notes: "",
    intakeWeight: "",
    ...defaults,
    ownFields: defaults?.ownFields ?? new Set<SharedIntakeFieldKey>(),
  };
}

/**
 * P3.1: Sammelaufnahme – gemeinsame Vorbelegung, individuelle Abweichungen.
 */
export default function AnimalBatchScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_VIEWPORT_BREAKPOINT;
  const coarse = useCoarseTarget();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const carriedNotice = useIntakeFollowupNotice(userId);
  const { organizations, loading: orgsLoading } = useMyOrganizations();
  const orgId = organizations[0]?.id;
  const {
    sites: orgSites,
    loading: sitesLoading,
    lastUsedId,
    accessFailed,
    remember,
  } = useIntakeCareSites(orgId);
  const { species: speciesOptions, listError: speciesListError } = useOrganizationSpecies(orgId);
  const { reasons } = useIntakeReasons(orgId);
  const createBatch = useCreateAnimalCasesBatch();
  const persistFinder = usePersistFinderForCases();
  const queryClient = useQueryClient();
  const finderMode = useFinderMode();
  const { isOnline } = useNetworkStatus();
  const [savedCaseIds, setSavedCaseIds] = useState<string[] | null>(null);
  const savedCaseIdsRef = useRef<string[] | null>(null);
  const weightSlots = useRef<Record<string, IntakeWeightSlot>>({});
  const [lockedWeights, setLockedWeights] = useState<Record<string, true>>({});
  const [weightSaving, setWeightSaving] = useState(false);
  const weightRightsTold = useRef(false);
  const intakeWasQueued = useRef(false);
  const [dropConfirm, setDropConfirm] = useState(false);
  const [privacyChoice, setPrivacyChoice] = useState(false);
  const [finderHeldLocal, setFinderHeldLocal] = useState(false);
  const offlineFinderBlocked = !isOnline && finderDraftHasPayload(finderMode.draft);
  const finderChoiceBlocked = offlineFinderBlocked || finderHeldLocal;
  useClearFinderDraftAfterSavedCase(savedCaseIds, finderMode.clearDraft);
  const scrollRef = useRef<ScrollView>(null);
  const fundortRef = useRef<TextInput>(null);
  const extraRef = useRef<TextInput>(null);
  const reasonRef = useRef<TextInput>(null);
  const finderNameRef = useRef<TextInput>(null);
  const noteRefs = useRef<(TextInput | null)[]>([]);
  const nameRefs = useRef<(TextInput | null)[]>([]);
  const weightRefs = useRef<(TextInput | null)[]>([]);
  const firstWeightFocusRef = useRef<"weight" | "name">("weight");

  const [clientBatchId, setClientBatchId] = useState(newClientId);
  const submitLock = useRef(false);
  const saveButtonRef = useRef<View>(null);
  const [siteFocus, setSiteFocus] = useState(0);
  const [dateFocus, setDateFocus] = useState(0);
  const [focusToken, setFocusToken] = useState(0);
  const caseNumbersRef = useRef<(string | null)[]>([]);
  const [receipt, setReceipt] = useState<{
    caseIds: string[];
    caseNumbers: (string | null)[];
    queued: boolean;
    arrivalNotice: string | null;
  } | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const animalRowMeasure = useBatchAnimalRow();
  const animalRow = animalRowMeasure.row;
  const [linkAsSiblings, setLinkAsSiblings] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<"muted" | "danger">("muted");
  const showMessage = (text: string | null, tone: "muted" | "danger" = "muted") => {
    setMessageTone(tone);
    setMessage(text);
  };
  const [partialLinkNotice, setPartialLinkNotice] = useState<string | null>(null);
  const [foundWarning, setFoundWarning] = useState<string | null>(null);
  const [foundOnFieldValid, setFoundOnFieldValid] = useState(true);
  const onFoundOnValidityChange = useCallback((valid: boolean) => {
    setFoundOnFieldValid(valid);
  }, []);

  // Shared defaults (step 1)
  const [sharedCareSiteId, setSharedCareSiteId] = useState<string | null>(null);
  const [sharedSpeciesId, setSharedSpeciesId] = useState<string | null>(null);
  const [sharedSpeciesFreeText, setSharedSpeciesFreeText] = useState("");
  const [speciesRequired, setSpeciesRequired] = useState(false);
  const [speciesFocus, setSpeciesFocus] = useState(0);
  const speciesIdRef = useRef<string | null>(null);
  const speciesFreeTextRef = useRef("");
  const onSharedSpeciesId = (id: string | null) => {
    speciesIdRef.current = id;
    setSharedSpeciesId(id);
    if (!intakeSpeciesMissing({ organizationSpeciesId: id, speciesFreeText: speciesFreeTextRef.current })) {
      setSpeciesRequired(false);
      setSpeciesFocus(0);
      setMessage((current) => (current === SPECIES_CHECK_HINT ? null : current));
    }
  };
  const onSharedSpeciesFreeText = (text: string) => {
    speciesFreeTextRef.current = text;
    setSharedSpeciesFreeText(text);
    if (!intakeSpeciesMissing({ organizationSpeciesId: speciesIdRef.current, speciesFreeText: text })) {
      setSpeciesRequired(false);
      setSpeciesFocus(0);
      setMessage((current) => (current === SPECIES_CHECK_HINT ? null : current));
    }
  };
  const rejectMissingSpecies = () => {
    showMessage(SPECIES_CHECK_HINT);
    setSpeciesRequired(true);
    setSpeciesFocus((n) => n + 1);
  };
  const siteTouched = useRef(false);
  const [sharedPlaceId, setSharedPlaceId] = useState<string | null>(null);
  const [sharedFoundOn, setSharedFoundOn] = useState<string | null>(() =>
    todayIsoLocal()
  );
  const [sharedFoundPlaceId, setSharedFoundPlaceId] = useState<string | null>(
    null
  );
  const [sharedFoundPlaceText, setSharedFoundPlaceText] = useState("");
  const [sharedFoundPlaceExtra, setSharedFoundPlaceExtra] = useState("");
  const [sharedReasons, setSharedReasons] = useState<Set<string>>(new Set());
  const [sharedReasonNote, setSharedReasonNote] = useState("");

  const [animals, setAnimals] = useState<AnimalDraft[]>(() => [
    blankAnimal(),
    blankAnimal(),
  ]);

  useEffect(() => {
    const a = assessFoundOn(sharedFoundOn);
    setFoundWarning(a.warning ?? null);
  }, [sharedFoundOn]);

  const siteOptions = useMemo(
    () => orgSites.map((s) => ({ value: s.id, label: s.name })),
    [orgSites],
  );

  useEffect(() => {
    if (sitesLoading || siteTouched.current) return;
    const next = prefillCareSiteId(orgSites, lastUsedId);
    setSharedCareSiteId((current) => (current === next ? current : next));
  }, [orgSites, lastUsedId, sitesLoading]);

  firstWeightFocusRef.current = batchWeightFocusTarget(!!lockedWeights[animals[0]?.clientCreateId]);

  useEffect(() => {
    if (receipt || step !== 2 || !shouldAutofocusIntakeWeight(coarse)) return;
    if (!consumeIntakeWeightFocus()) return;
    const id = requestAnimationFrame(() => {
      if (firstWeightFocusRef.current === "name") nameRefs.current[0]?.focus();
      else weightRefs.current[0]?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [receipt, coarse, focusToken, step]);

  const nodesQ = useQuery({
    queryKey: queryKeys.careSites.nodes(sharedCareSiteId ?? ""),
    queryFn: () => CareSitesApi.listPlaceNodes(sharedCareSiteId!),
    enabled: !!sharedCareSiteId && step === 1,
  });

  const placeOptions = useMemo(() => {
    const nodes = nodesQ.data ?? [];
    return [
      { value: null as string | null, label: "Noch nicht zugeordnet" },
      ...nodes
        .filter((n) => n.is_occupiable)
        .map((n) => ({ value: n.id, label: n.name })),
    ];
  }, [nodesQ.data]);

  const colGap = theme.space.md;
  const half = wide
    ? ({ flex: 1, minWidth: 0 } as const)
    : ({ width: "100%" } as const);

  const toggleSharedReason = (id: string) => {
    setSharedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sharedIntake = (): SharedIntakeFields => ({
    careSiteId: sharedCareSiteId,
    placeId: sharedPlaceId,
    foundOn: sharedFoundOn,
    foundPlaceId: sharedFoundPlaceId,
    foundPlaceText: sharedFoundPlaceText,
    foundPlaceExtra: sharedFoundPlaceExtra,
    selectedReasons: sharedReasons,
    reasonNote: sharedReasonNote,
  });

  const goToIndividuals = () => {
    // Bildschirmreihenfolge: Art, Pflegestelle, Funddatum.
    if (speciesFreeTextRef.current.trim().length > SPECIES_FREE_TEXT_MAX) {
      showMessage(SPECIES_FREE_TEXT_TOO_LONG, "danger");
      return;
    }
    if (
      intakeSpeciesMissing({
        organizationSpeciesId: speciesIdRef.current,
        speciesFreeText: speciesFreeTextRef.current,
      })
    ) {
      rejectMissingSpecies();
      return;
    }
    showMessage(null);
    if (!sharedCareSiteId) {
      showMessage("Bitte eine Pflegestelle wählen.");
      return;
    }
    if (!foundOnFieldValid) {
      showMessage("Das Funddatum ist ungültig.");
      return;
    }
    const dateAssessed = assessFoundOn(sharedFoundOn);
    if (!dateAssessed.ok) {
      showMessage(dateAssessed.error ?? "Ungültiges Funddatum.");
      return;
    }
    setAnimals((prev) => applySharedIntake(prev, sharedIntake()));
    setStep(2);
  };

  const updateAnimal = (idx: number, patch: Partial<AnimalDraft>) => {
    setAnimals((prev) =>
      prev.map((a, i) => {
        if (i !== idx) return a;
        return { ...a, ...patch, ownFields: markOwnIntakeFields(a.ownFields, patch) };
      }),
    );
  };

  const addAnimal = () => {
    const [added] = applySharedIntake([blankAnimal()], sharedIntake());
    setAnimals((prev) => [...prev, added]);
  };

  const removeAnimal = (idx: number) => {
    setAnimals((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const onSubmit = async (dropFinder = false) => {
    if (
      submitLock.current ||
      createBatch.isPending ||
      weightSaving ||
      persistFinder.isPending
    ) {
      return;
    }
    setPartialLinkNotice(null);
    if (dropFinder) {
      setDropConfirm(false);
      setPrivacyChoice(false);
    }
    // Art zuerst, dann die Finder-Hinweise. Danach Pflegestelle, Gewicht, Datum.
    if (speciesFreeTextRef.current.trim().length > SPECIES_FREE_TEXT_MAX) {
      setStep(1);
      showMessage(SPECIES_FREE_TEXT_TOO_LONG, "danger");
      return;
    }
    if (
      intakeSpeciesMissing({
        organizationSpeciesId: speciesIdRef.current,
        speciesFreeText: speciesFreeTextRef.current,
      })
    ) {
      setStep(1);
      rejectMissingSpecies();
      return;
    }
    showMessage(null);
    const draft = dropFinder ? emptyFinderDraft() : finderMode.draft;
    if (!dropFinder && finderPayloadWithoutName(draft)) {
      showMessage(FINDER_NAME_SAVE_HINT);
      finderMode.requestScrollToFinder();
      return;
    }
    if (!dropFinder && !isOnline && finderDraftHasPayload(draft)) {
      showMessage(FINDER_OFFLINE_FINDER);
      return;
    }
    if (!dropFinder && finderHeldLocal) {
      showMessage(FINDER_OFFLINE_FINDER);
      return;
    }
    if (!orgId) {
      showMessage("Keine aktive Organisation.");
      return;
    }
    if (animals.length < 2) {
      showMessage("Mindestens zwei Tiere nötig.");
      return;
    }
    for (let i = 0; i < animals.length; i++) {
      if (!animals[i].careSiteId) {
        showMessage(`Tier ${i + 1}: Pflegestelle fehlt.`);
        return;
      }
    }
    for (let i = 0; i < animals.length; i++) {
      if (weightSlots.current[animals[i].clientCreateId]) continue;
      const weightError = intakeWeightErrorText(animals[i].intakeWeight);
      if (weightError) {
        const text = `Tier ${i + 1}: ${weightError}`;
        showMessage(uiErrorMessage(new Error(text), text), "danger");
        return;
      }
    }
    if (!foundOnFieldValid) {
      setStep(1);
      showMessage("Das Funddatum ist ungültig.");
      return;
    }
    for (let i = 0; i < animals.length; i++) {
      const dateAssessed = assessFoundOn(animals[i].foundOn);
      if (!dateAssessed.ok) {
        showMessage(`Tier ${i + 1}: ${dateAssessed.error ?? "Ungültiges Funddatum."}`);
        return;
      }
    }
    if (!finderModeAllowsFinalize(finderMode.active)) {
      showMessage(FINDER_STILL_ACTIVE);
      return;
    }
    submitLock.current = true;
    try {
      if (!dropFinder && finderSaveBlockedWithoutPrivacy(draft)) {
        setPrivacyChoice(true);
        return;
      }
      if (!dropFinder && finderSaveBlockedWithoutProvision(draft)) {
        setPrivacyChoice(false);
        finderMode.noteProvisionAttempt();
        finderMode.requestScrollToFinder();
        return;
      }
      setPrivacyChoice(false);
      let caseIds = savedCaseIdsRef.current;
      if (!caseIds) {
        const species = intakeSpeciesForSave({
          organizationSpeciesId: speciesIdRef.current,
          speciesFreeText: speciesFreeTextRef.current,
        });
        const created = await createBatch.mutateAsync({
          organizationId: orgId,
          clientBatchId,
          linkAsSiblings,
          animals: animals.map((a) => {
            const dateAssessed = assessFoundOn(a.foundOn);
            const place = foundPlaceForSave({
              placeId: a.foundPlaceId,
              typed: a.foundPlaceText,
              extra: a.foundPlaceExtra,
            });
            return {
              clientCreateId: a.clientCreateId,
              careSiteId: a.careSiteId!,
              placeId: a.placeId,
              name: a.name.trim() || null,
              sex: a.sex,
              ageClass: a.ageClass,
              foundOn: dateAssessed.date,
              foundPlaceId: place.foundPlaceId,
              foundPlaceText: place.foundPlaceText,
              intakeReasonIds: [...a.selectedReasons],
              intakeReasonNote: a.reasonNote.trim() || null,
              generalNotes: a.notes.trim() || null,
              organizationSpeciesId: species.organizationSpeciesId,
              speciesFreeText: species.speciesFreeText,
            };
          }),
        });
        if (created.length === 0) {
          showMessage(FINDER_BATCH_FAILED, "danger");
          return;
        }
        caseIds = created.map((c) => c.id);
        caseNumbersRef.current = created.map((row) => {
          const number = row.case_number;
          return typeof number === "string" && number.trim().length > 0 ? number.trim() : null;
        });
        savedCaseIdsRef.current = caseIds;
        setSavedCaseIds(caseIds);
        if (intakeQueuedLocally(created)) intakeWasQueued.current = true;
      }
      const recordedAt = new Date().toISOString();
      const slots: IntakeWeightSlot[] = [];
      const newlyLocked: Record<string, true> = {};
      for (let i = 0; i < animals.length && i < caseIds.length; i++) {
        const animal = animals[i];
        const parsed = parseIntakeWeight(animal.intakeWeight);
        const existing = weightSlots.current[animal.clientCreateId];
        const grams = existing?.grams ?? (parsed.ok ? parsed.grams : null);
        if (grams == null) continue;
        if (!existing) {
          weightSlots.current[animal.clientCreateId] = {
            caseId: caseIds[i],
            clientEntryId: newClientId(),
            recordedAt,
            grams,
            saved: false,
            abandoned: false,
          };
        } else {
          existing.caseId = caseIds[i];
        }
        newlyLocked[animal.clientCreateId] = true;
        slots.push(weightSlots.current[animal.clientCreateId]);
      }
      if (Object.keys(newlyLocked).length > 0) {
        setLockedWeights((prev) => ({ ...prev, ...newlyLocked }));
      }
      let outcome: IntakeWeightOutcome = { kind: "ok" };
      if (slots.length > 0) {
        setWeightSaving(true);
        try {
          outcome = await settleIntakeWeights(slots, async (current) => {
            await CareProtocolApi.submitEntry({
              caseId: current.caseId,
              clientEntryId: current.clientEntryId,
              recordedAt: current.recordedAt,
              events: [intakeWeightEvent(current.grams)],
            });
            invalidateIntakeWeightQueries(queryClient, current.caseId);
          });
        } finally {
          setWeightSaving(false);
        }
      }
      if (outcome.kind === "retry") {
        showMessage(uiErrorMessage(outcome.error, INTAKE_WEIGHT_RETRY_FALLBACK), "danger");
        return;
      }
      if (outcome.kind === "rights" && !weightRightsTold.current) {
        weightRightsTold.current = true;
        showMessage(
          uiErrorMessage(new Error(INTAKE_WEIGHT_RIGHTS_MESSAGE), INTAKE_WEIGHT_RIGHTS_MESSAGE),
          "danger",
        );
        return;
      }
      if (!dropFinder && intakeWasQueued.current && finderDraftHasPayload(draft)) {
        setFinderHeldLocal(true);
        return;
      }
      let arrivalNotice: string | null = null;
      if (!dropFinder && isFinderDraftReady(draft)) {
        try {
          const saved = await persistFinder.mutateAsync({
            organizationId: orgId,
            caseIds,
            contact: {
              organizationId: orgId,
              displayName: draft.displayName.trim(),
              notes: draft.notes.trim() || null,
              phone: draft.phone.trim() || null,
              email: draft.email.trim() || null,
              address: draft.address.trim() || null,
              preferredChannel: draft.preferredChannel.trim() || null,
              privacyNoticeVersion: draft.privacyNoticeVersion,
              privacyProvisionKind: draft.privacyProvisionKind,
              privacyDocumented: draft.privacyDocumented,
              clientContactId: draft.clientContactId,
              matchedContactId: draft.matchedContactId,
              reuseBaseline: draft.matchedBaseline,
            },
          });
          if (saved?.identityLocked) {
            const restore = saved.restoreContact;
            setPartialLinkNotice(
              restore ? FINDER_PARTIAL_IDENTITY_RESTORED : FINDER_PARTIAL_IDENTITY_LOCKED,
            );
            if (restore) {
              finderMode.updateDraft({
                clientContactId: restore.clientContactId,
                matchedContactId: restore.matchedContactId ?? null,
                matchedBaseline: restore.matchedContactId
                  ? {
                      displayName: restore.displayName,
                      phone: restore.phone ?? "",
                      email: restore.email ?? "",
                      privacyDocumented: restore.privacyDocumented,
                    }
                  : null,
                displayName: restore.displayName,
                phone: restore.phone ?? "",
                email: restore.email ?? "",
                address: restore.address ?? "",
                notes: restore.notes ?? "",
                preferredChannel: restore.preferredChannel ?? "",
                privacyNoticeVersion:
                  restore.privacyNoticeVersion || finderMode.draft.privacyNoticeVersion,
                privacyProvisionKind: restore.privacyProvisionKind ?? null,
              });
              if (restore.privacyDocumented) {
                finderMode.updateDraft({ privacyDocumented: true });
              }
            }
            return;
          }
          arrivalNotice = saved?.notice ?? null;
        } catch {
          showMessage(FINDER_SAVED_FINDER_FAILED, "danger");
          finderMode.requestScrollToFinder();
          return;
        }
      }
      setFinderHeldLocal(false);
      savedCaseIdsRef.current = null;
      weightSlots.current = {};
      setLockedWeights({});
      setSavedCaseIds(null);
      clearIntakeFollowupNotice();
      rememberIntakeCases(userId, caseIds);
      const rememberedSite = animals[0]?.careSiteId;
      if (rememberedSite) remember(rememberedSite);
      setReceipt({
        caseIds,
        caseNumbers: caseNumbersRef.current,
        queued: intakeWasQueued.current,
        arrivalNotice,
      });
    } catch (error: unknown) {
      showMessage(intakeErrorMessage(error, FINDER_BATCH_FAILED), "danger");
    } finally {
      submitLock.current = false;
    }
  };

  const takeMore = () => {
    armIntakeFollowup({
      userId,
      caseIds: receipt?.caseIds ?? [],
      text: receipt?.arrivalNotice ?? null,
    });
    if (shouldAutofocusIntakeWeight(coarse)) requestIntakeWeightFocus();
    finderMode.clearDraft();
    // Folgetiere meist dieselbe Art: die gemeinsame Art bleibt gesetzt.
    // Zurück zeigt denselben Chip oder denselben Freitext.
    setSpeciesRequired(false);
    setSpeciesFocus(0);
    setReceipt(null);
    setFocusToken((n) => n + 1);
    setClientBatchId(newClientId());
    showMessage(null);
    setPartialLinkNotice(null);
    setDropConfirm(false);
    setPrivacyChoice(false);
    setFinderHeldLocal(false);
    savedCaseIdsRef.current = null;
    caseNumbersRef.current = [];
    weightSlots.current = {};
    setLockedWeights({});
    setSavedCaseIds(null);
    intakeWasQueued.current = false;
    weightRightsTold.current = false;
    const [first, second] = applySharedIntake(
      [blankAnimal(), blankAnimal()],
      sharedIntake(),
    );
    setAnimals([first, second]);
    setStep(2);
  };

  const openFirstCase = () => {
    if (!receipt) return;
    finderMode.clearDraft();
    router.replace(finderCaseHref(receipt.caseIds[0], receipt.arrivalNotice) as never);
  };

  return (
    <Screen
      title="Mehrere aufnehmen"
      scroll
      scrollRef={scrollRef}
      contentStyle={{ paddingBottom: theme.space.xxl }}
    >
      <View style={{ gap: theme.space.md }}>
        {receipt ? (
          <View style={{ gap: theme.space.sm }}>
            <Text
              accessibilityLiveRegion="polite"
              style={{ color: theme.color.text, fontSize: theme.fontSize.body }}
            >
              {batchIntakeReceiptText({
                count: receipt.caseIds.length,
                caseNumbers: receipt.caseNumbers,
                queued: receipt.queued,
              })}
            </Text>
            <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
              {INTAKE_KEPT_LINE}
            </Text>
            {receipt.arrivalNotice ? (
              <Text
                accessibilityLiveRegion="polite"
                style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
              >
                {receipt.arrivalNotice}
              </Text>
            ) : null}
            <View
              style={{
                flexDirection: wide ? "row" : "column",
                gap: theme.space.sm,
              }}
            >
              <AppButton
                onPress={takeMore}
                style={coarse ? { minHeight: theme.control.heightTouch } : undefined}
              >
                Weitere Tiere aufnehmen
              </AppButton>
              <AppButton
                variant="outline"
                onPress={openFirstCase}
                style={coarse ? { minHeight: theme.control.heightTouch } : undefined}
              >
                Akte öffnen
              </AppButton>
            </View>
          </View>
        ) : (
        <>
        {carriedNotice ? (
          <Text
            accessibilityLiveRegion="polite"
            style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
          >
            {carriedNotice}
          </Text>
        ) : null}
        <Text
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.small,
          }}
        >
          Sammelaufnahme: gemeinsame Vorbelegung, danach individuelle Anpassung.
        </Text>

        {step === 1 ? (
          <>
            <SectionCard
              title="Gemeinsame Angaben"
              bodyStyle={{ paddingVertical: theme.space.sm }}
            >
              <Text
                style={{
                  color: theme.color.muted,
                  fontSize: theme.fontSize.small,
                  marginBottom: theme.space.sm,
                }}
              >
                Diese Werte werden für alle Tiere vorbelegt. Im nächsten Schritt
                kannst du jedes Tier einzeln anpassen.
              </Text>
              {viewWhileLoading(orgsLoading, !orgId) === "loading" ||
              (orgId && sitesLoading) ? (
                <Text style={{ color: theme.color.muted }}>{LOADING_LABEL}</Text>
              ) : !orgId ? (
                <EmptyState
                  title="Keine aktive Organisation."
                  hint="Ohne Verein ist eine Sammelaufnahme nicht möglich."
                />
              ) : accessFailed ? (
                <EmptyState
                  title="Pflegestellen nicht geladen."
                  hint="Die Stellen mit Schreibrecht konnten nicht gelesen werden."
                />
              ) : siteOptions.length === 0 ? (
                <EmptyState
                  title="Keine Pflegestelle mit Schreibrecht."
                  hint="Eine Aufnahme ist nur in einer Stelle möglich, für die du Schreibrecht hast."
                />
              ) : (
              <View style={{ gap: theme.space.sm }}>
                <IntakeSpeciesField
                  options={speciesOptions}
                  speciesId={sharedSpeciesId}
                  freeText={sharedSpeciesFreeText}
                  onChangeSpeciesId={onSharedSpeciesId}
                  onChangeFreeText={onSharedSpeciesFreeText}
                  listError={speciesListError}
                  requiredError={speciesRequired}
                  focusRequest={speciesFocus}
                  scrollRef={scrollRef}
                  onFreeTextSubmit={() => {
                    if (!sharedCareSiteId) setSiteFocus((n) => n + 1);
                    else setDateFocus((n) => n + 1);
                  }}
                />
                <View
                  style={{
                    flexDirection: wide ? "row" : "column",
                    gap: colGap,
                  }}
                >
                  <View style={half}>
                    <FloatingLabelListPicker
                      label="Pflegestelle"
                      options={siteOptions}
                      value={sharedCareSiteId}
                      focusRequest={siteFocus}
                      onChange={(v) => {
                        siteTouched.current = true;
                        setSharedCareSiteId(v);
                        setSharedPlaceId(null);
                      }}
                      required
                      placeholder="Pflegestelle wählen…"
                    />
                  </View>
                  <View style={half}>
                    <FloatingLabelListPicker
                      label="Unterbringungsplatz"
                      options={placeOptions}
                      value={sharedPlaceId}
                      onChange={setSharedPlaceId}
                      placeholder="Optional"
                      hint={INTAKE_PLACE_HINT}
                    />
                  </View>
                </View>
                <View
                  style={{
                    flexDirection: wide ? "row" : "column",
                    gap: colGap,
                  }}
                >
                  <View style={half}>
                    <FloatingLabelDatePicker
                      label="Funddatum"
                      value={sharedFoundOn}
                      onChange={setSharedFoundOn}
                      onValidityChange={onFoundOnValidityChange}
                      optional
                      allowClear
                      maxIso={todayIsoLocal()}
                      placeholder="Heute – oder entfernen"
                      focusRequest={dateFocus}
                      onSubmitEditing={() => fundortRef.current?.focus()}
                    />
                  </View>
                  <View style={half}>
                    <GeoPlaceAutocompleteField
                      label="Fundort"
                      inputRef={fundortRef}
                      valuePlaceId={sharedFoundPlaceId}
                      valueText={sharedFoundPlaceText}
                      onChangeText={(t) => {
                        setSharedFoundPlaceText(t);
                        setSharedFoundPlaceId(null);
                      }}
                      onSelect={(place) => {
                        setSharedFoundPlaceId(place.id);
                        setSharedFoundPlaceText(formatPlaceLine(place));
                      }}
                      placeholder="Fundort oder PLZ suchen"
                      hint="Ort oder Postleitzahl wählen. Eine Straße ist nicht nötig."
                      warning={
                        foundPlaceNeedsHint(sharedFoundPlaceId, sharedFoundPlaceText)
                          ? FUNDORT_UNSELECTED_HINT
                          : null
                      }
                      returnKeyType="next"
                      blurOnSubmit={false}
                      onSubmitEditing={() => extraRef.current?.focus()}
                    />
                  </View>
                </View>
                {foundWarning ? (
                  <Text style={{ color: theme.color.warningText, fontSize: 12 }}>
                    {foundWarning}
                  </Text>
                ) : null}
                <FloatingLabelInput
                  ref={extraRef}
                  label="Ergänzung zum Fundort"
                  value={sharedFoundPlaceExtra}
                  onChangeText={setSharedFoundPlaceExtra}
                  returnKeyType="next"
                  blurOnSubmit={false}
                  onSubmitEditing={() => reasonRef.current?.focus()}
                />
                <Text
                  style={{
                    color: theme.color.textSecondary,
                    fontSize: 13,
                    fontWeight: "600",
                  }}
                >
                  Aufnahmegründe
                </Text>
                <IntakeReasonChips
                  reasons={reasons}
                  selected={sharedReasons}
                  onToggle={toggleSharedReason}
                />
                <FloatingLabelInput
                  ref={reasonRef}
                  label="Ergänzung zum Aufnahmegrund"
                  value={sharedReasonNote}
                  onChangeText={setSharedReasonNote}
                  returnKeyType="done"
                />
                <Pressable
                  onPress={() => setLinkAsSiblings((v) => !v)}
                  {...(Platform.OS === "web"
                    ? {
                        onKeyDown: (event: {
                          key?: string;
                          repeat?: boolean;
                          nativeEvent?: { key?: string; repeat?: boolean };
                          preventDefault?: () => void;
                        }) => {
                          if (event.repeat ?? event.nativeEvent?.repeat) return;
                          const key = event.key ?? event.nativeEvent?.key;
                          if (key !== " " && key !== "Spacebar") return;
                          event.preventDefault?.();
                          setLinkAsSiblings((current) => !current);
                        },
                      }
                    : {})}
                  accessibilityRole="checkbox"
                  accessibilityLabel="Als Geschwistergruppe verknüpfen"
                  accessibilityState={{ checked: linkAsSiblings }}
                  aria-checked={linkAsSiblings}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: theme.space.sm,
                    paddingVertical: theme.space.xs,
                  }}
                >
                  <View
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 4,
                      borderWidth: 2,
                      borderColor: theme.color.primary,
                      backgroundColor: linkAsSiblings
                        ? theme.color.primary
                        : "transparent",
                    }}
                  />
                  <Text style={{ color: theme.color.text, flex: 1 }}>
                    Als Geschwistergruppe verknüpfen
                  </Text>
                </Pressable>
              </View>
              )}
            </SectionCard>
            {message ? (
              <Text
                {...intakeStatusLiveProps(message, messageTone)}
                style={{ color: messageTone === "danger" ? theme.color.dangerText : theme.color.muted }}
              >
                {message}
              </Text>
            ) : null}
            {!foundOnFieldValid ? (
              <Text
                accessibilityLiveRegion="polite"
                style={{ color: theme.color.dangerText, fontSize: theme.fontSize.small }}
              >
                {FUND_DATE_CHECK_HINT}
              </Text>
            ) : null}
            <AppButton
              accessibilityRole="button"
              onPress={goToIndividuals}
              disabled={!orgId || !foundOnFieldValid}
            >
              Weiter zu den Individuen
            </AppButton>
          </>
        ) : (
          <>
            <SectionCard
              title={`Individuen (${animals.length})`}
              bodyStyle={{ paddingVertical: theme.space.sm }}
            >
              <View
                testID="batch-shared-summary"
                accessibilityLabel="Gemeinsame Angaben"
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: theme.space.sm,
                  marginBottom: theme.space.sm,
                }}
              >
                <Text style={{ color: theme.color.text, fontSize: theme.fontSize.small }}>
                  {`Pflegestelle: ${
                    siteOptions.find((site) => site.value === sharedCareSiteId)?.label ?? "–"
                  }`}
                </Text>
                <Text style={{ color: theme.color.text, fontSize: theme.fontSize.small }}>
                  {`Gemeinsames Funddatum: ${
                    sharedFoundOn ? formatIsoDateDE(sharedFoundOn) : "–"
                  }`}
                </Text>
                <Text style={{ color: theme.color.text, fontSize: theme.fontSize.small }}>
                  {`Fundort: ${sharedFoundPlaceText.trim() || "–"}`}
                </Text>
                {intakeSharedSpeciesLabel(sharedSpeciesId, sharedSpeciesFreeText, speciesOptions) ? (
                  <Text style={{ color: theme.color.text, fontSize: theme.fontSize.small }}>
                    {`Art: ${intakeSharedSpeciesLabel(sharedSpeciesId, sharedSpeciesFreeText, speciesOptions)}`}
                  </Text>
                ) : null}
                <AppButton
                  variant="ghost"
                  accessibilityRole="button"
                  accessibilityLabel="Gemeinsame Angaben ändern"
                  onPress={() => setStep(1)}
                  style={coarse ? { minHeight: theme.control.heightTouch } : undefined}
                >
                  Ändern
                </AppButton>
              </View>
              <View
                testID="batch-individuals"
                ref={animalRowMeasure.ref}
                onLayout={animalRowMeasure.onLayout}
                style={{ gap: theme.space.md }}
              >
                {animals.map((a, idx) => (
                  <BatchAnimalEditor
                    key={a.clientCreateId}
                    index={idx}
                    count={animals.length}
                    name={a.name}
                    sex={a.sex}
                    ageClass={a.ageClass}
                    intakeWeight={a.intakeWeight}
                    notes={a.notes}
                    weightLocked={!!lockedWeights[a.clientCreateId]}
                    row={animalRow}
                    onChange={(patch) => updateAnimal(idx, patch)}
                    onRemove={animals.length > 2 ? () => removeAnimal(idx) : undefined}
                    weightRef={(node) => {
                      weightRefs.current[idx] = node;
                    }}
                    nameRef={(node) => {
                      nameRefs.current[idx] = node;
                    }}
                    noteRef={(node) => {
                      noteRefs.current[idx] = node;
                    }}
                    onFocusNext={() => {
                      const next = animals[idx + 1];
                      if (!next) return;
                      const target = batchWeightFocusTarget(!!lockedWeights[next.clientCreateId]);
                      if (target === "name") nameRefs.current[idx + 1]?.focus();
                      else weightRefs.current[idx + 1]?.focus();
                    }}
                    onFocusSave={() => {
                      const node = saveButtonRef.current as { focus?: () => void } | null;
                      node?.focus?.();
                    }}
                  />
                ))}
              </View>
              <AppButton
                variant="outline"
                onPress={addAnimal}
                style={{
                  marginTop: theme.space.sm,
                  ...(coarse ? { minHeight: theme.control.heightTouch } : null),
                }}
              >
                Weiteres Tier hinzufügen
              </AppButton>
            </SectionCard>
            <FinderIntakeCard
              organizationId={orgId}
              scrollRef={scrollRef}
              nameInputRef={finderNameRef}
              intro="Ein Finder-Entwurf gilt für alle Tiere dieser Sammelaufnahme. Nach der Rückgabe prüfst du ihn hier."
            />
            {message ? (
              <Text
                {...intakeStatusLiveProps(message, messageTone)}
                style={{ color: messageTone === "danger" ? theme.color.dangerText : theme.color.muted }}
              >
                {message}
              </Text>
            ) : null}
            {partialLinkNotice ? (
              <Text
                accessibilityLiveRegion="polite"
                style={{ color: theme.color.warningText }}
              >
                {partialLinkNotice}
              </Text>
            ) : null}
            <FinderPrivacySaveChoice
              visible={privacyChoice}
              caseAlreadySaved={savedCaseIds != null}
              onHandBack={() => {
                setPrivacyChoice(false);
                void finderMode.start();
              }}
              onDrop={() => {
                void onSubmit(true);
              }}
            />
            <FinderOfflineChoice
              blocked={finderChoiceBlocked}
              confirming={dropConfirm}
              onRequest={() => setDropConfirm(true)}
              onCancel={() => setDropConfirm(false)}
              onConfirm={() => {
                void onSubmit(true);
              }}
            />
            <View
              style={{
                flexDirection: wide ? "row" : "column",
                gap: theme.space.sm,
              }}
            >
              <AppButton
                variant="secondary"
                onPress={() => {
                  setSpeciesFocus(0);
                  setStep(1);
                }}
                style={coarse ? { minHeight: theme.control.heightTouch } : undefined}
              >
                Zurück
              </AppButton>
              <AppButton
                ref={saveButtonRef}
                accessibilityRole="button"
                onPress={() => {
                  void onSubmit(false);
                }}
                style={coarse ? { minHeight: theme.control.heightTouch } : undefined}
                disabled={
                  finderChoiceBlocked ||
                  privacyChoice ||
                  createBatch.isPending ||
                  weightSaving ||
                  persistFinder.isPending ||
                  !orgId ||
                  !foundOnFieldValid ||
                  !finderModeAllowsFinalize(finderMode.active)
                }
              >
                {createBatch.isPending || weightSaving
                  ? "Speichern…"
                  : `${animals.length} Tiere aufnehmen`}
              </AppButton>
            </View>
          </>
        )}
        </>
        )}
      </View>
    </Screen>
  );
}
